import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  TablePagination
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

// Cari hesap arayüzü
interface Account {
  id: string;
  companyName: string;
  authorizedPerson: string;
  mobilePhone: string;
  isActive: boolean;
}

interface PassiveAccountsProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  onAccountActivated: () => void;
  /** Verilirse sadece bu şubenin pasif carileri listelenir (manager paneli) */
  branchFilterId?: string | null;
}

const PassiveAccounts: React.FC<PassiveAccountsProps> = ({
  open,
  onClose,
  companyId,
  onAccountActivated,
  branchFilterId = null
}) => {
  const { t } = useTranslation('accounting');
  const [passiveAccounts, setPassiveAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Pasif hesapları yükleme - SQL'den
  useEffect(() => {
    const loadPassiveAccounts = async () => {
      if (!open || !companyId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log('Pasif hesaplar SQL\'den yükleniyor...');
        
        // SQL API'den tüm hesapları al
        const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
        
        const result = response.data;
        const sqlAccounts = result.data || result;
        
        // Pasif hesapları filtrele (şube filtresi varsa sadece o şube)
        const passiveAccountsList: Account[] = sqlAccounts
          .filter((data: any) => data.isActive === false)
          .filter((data: any) => !branchFilterId || data.branchId === branchFilterId)
          .map((data: any) => ({
            id: data.id.toString(), // SQL ID'yi string'e çevir
            companyName: data.companyName || '',
            authorizedPerson: data.authorizedPerson || '',
            mobilePhone: data.mobilePhone || '',
            isActive: false
          }));
        
        // Hesapları isme göre sırala
        passiveAccountsList.sort((a, b) => a.companyName.localeCompare(b.companyName));
        
        console.log(`✅ ${passiveAccountsList.length} pasif hesap bulundu (SQL)`);
        
        setPassiveAccounts(passiveAccountsList);
        setLoading(false);
        
      } catch (error) {
        console.error('Pasif hesaplar yüklenirken hata:', error);
        setError(handleApiError(error));
        setLoading(false);
      }
    };
    
    loadPassiveAccounts();
    
    // Cleanup
    return () => {};
  }, [open, companyId, branchFilterId]);

  // Hesabı aktif hale getirme - SQL API
  const handleActivateAccount = async (account: Account) => {
    if (!companyId) {
      console.error('Şirket ID bulunamadı');
      return;
    }

    try {
      // Account ID sayısal olmalı (SQL ID)
      const numericId = parseInt(account.id, 10);
      if (isNaN(numericId)) {
        throw new Error('Geçersiz hesap ID formatı');
      }

      // ✅ MULTI-TENANT: companyId parametresi ekle
      if (!companyId) {
        throw new Error('CompanyId bulunamadı');
      }

      // SQL API'ye aktifleştirme isteği gönder
      await accountingApi.post(ACCOUNTING_API.ACCOUNT_ACTIVATE(numericId));

      console.log(`✅ ${account.companyName} hesabı aktif hale getirildi (SQL)`);
      onAccountActivated(); // Ana sayfayı güncelle
    } catch (error: any) {
      console.error('Hesap aktifleştirilirken hata:', error);
      setError(handleApiError(error));
    }
  };

  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sayfalandırılmış hesaplar
  const paginatedAccounts = passiveAccounts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleCloseModal = () => {
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6">{t('current.modals.passiveAccounts.title')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GuideButton guideKey="accounts.passive" color="inherit" />
            <IconButton edge="end" color="inherit" onClick={handleCloseModal} aria-label={t('current.actions.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : passiveAccounts.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {t('current.modals.passiveAccounts.empty')}
              </Typography>
            </Box>
          ) : (
            <Paper elevation={0} sx={{ mt: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('current.table.columns.companyName')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('current.table.columns.authorizedPerson')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('current.table.columns.mobilePhone')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('current.table.columns.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedAccounts.map((account) => (
                      <TableRow key={account.id} hover>
                        <TableCell>{account.companyName}</TableCell>
                        <TableCell>{account.authorizedPerson || '-'}</TableCell>
                        <TableCell>{account.mobilePhone || '-'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('current.modals.passiveAccounts.activate')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleActivateAccount(account)}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {passiveAccounts.length > 10 && (
                <TablePagination
                  component="div"
                  count={passiveAccounts.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage={t('dataTable.rowsPerPage')}
                  labelDisplayedRows={({ from, to, count }) => t('current.modals.passiveAccounts.displayedRows', { count, from, to })}
                />
              )}
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal}>{t('current.actions.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PassiveAccounts; 